import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { Plus, FileText, MapPin, X, Loader, Eye, AlertCircle, Download, Copy, Edit } from "lucide-react";
import Swal from "sweetalert2";
import { jsPDF } from "jspdf";
import logoImg from "../../../../images/logo1_cicada.png";
import {
    getMyMissionRequestsRequest,
    createMissionRequestRequest,
    duplicateMissionRequestRequest,
    cancelMissionRequestRequest,
} from "../../../../redux/actions/missionRequestActions";
import MissionInlineForm, { calculateDurationDays } from "./MissionInlineForm";
import { API_BASE_URL } from "../../../../../api";
import "../../parametres/parametresStyles/ParamConges.css";

const MesOrdres = () => {
    const dispatch = useDispatch();
    const { myRequests, loading } = useSelector((state) => state.missionRequests || {
        myRequests: [],
        loading: false,
    });
    const user = useSelector((state) => state.auth.user);

    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);

    const headers = useMemo(() => ({
        uid: sessionStorage.getItem("uid"),
        Authorization: `Bearer ${sessionStorage.getItem("token")}`,
        "X-Tenant-Id": sessionStorage.getItem("selectedCompanyId"),
        "Content-Type": "application/json",
        Accept: "application/json",
    }), []);

    useEffect(() => {
        dispatch(getMyMissionRequestsRequest());
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        return new Date(dateStr).toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    };

    const getStatutConfig = (statut) => {
        const config = {
            PENDING_MANAGER: {
                label: "En attente du manager",
                fullLabel: "En attente de la validation du manager",
                color: "#b45309",
                bg: "#fef3c7",
                border: "#fcd34d",
                dot: "#f59e0b",
            },
            PENDING_ADMIN: {
                label: "En attente de l'admin RH",
                fullLabel: "En attente de la validation RH",
                color: "#b45309",
                bg: "#fef3c7",
                border: "#fcd34d",
                dot: "#f59e0b",
            },
            APPROVED: {
                label: "Approuvée",
                fullLabel: "Approuvée",
                color: "#166534",
                bg: "#dcfce7",
                border: "#86efac",
                dot: "#16a34a",
            },
            REJECTED: {
                label: "Rejetée",
                fullLabel: "Rejetée",
                color: "#991b1b",
                bg: "#fee2e2",
                border: "#fca5a5",
                dot: "#ef4444",
            },
            CANCELLED: {
                label: "Annulée",
                fullLabel: "Annulée",
                color: "#374151",
                bg: "#f3f4f6",
                border: "#d1d5db",
                dot: "#9ca3af",
            },
        };
        return config[statut] || { label: statut, fullLabel: statut, color: "#374151", bg: "#f3f4f6", border: "#d1d5db", dot: "#9ca3af" };
    };

    const getStatutBadge = (statut) => {
        const c = getStatutConfig(statut);
        return (
            <span style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 10px",
                borderRadius: 999,
                fontSize: 11,
                fontWeight: 600,
                color: c.color,
                background: c.bg,
                border: `1px solid ${c.border}`,
                whiteSpace: "nowrap",
            }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
                {c.label}
            </span>
        );
    };

    const handleSubmit = (payload) => {
        dispatch(createMissionRequestRequest(payload));
        setShowModal(false);
    };

    const handleCancel = (id) => {
        Swal.fire({
            title: "Confirmation",
            text: "Voulez-vous vraiment annuler cette demande ?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#ef4444",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, annuler",
            cancelButtonText: "Non",
        }).then((result) => {
            if (result.isConfirmed) dispatch(cancelMissionRequestRequest(id));
        });
    };

    const handleDuplicate = (demande) => {
        Swal.fire({
            title: "Dupliquer l'ordre de mission ?",
            text: `Une copie de "${demande.ordreMissionLibelle || demande.ordreMissionCode}" sera créée avec les mêmes informations.`,
            icon: "question",
            showCancelButton: true,
            confirmButtonColor: "var(--admin-accent)",
            cancelButtonColor: "#6b7280",
            confirmButtonText: "Oui, dupliquer",
            cancelButtonText: "Annuler",
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(duplicateMissionRequestRequest(demande.id));
            }
        });
    };

    const canEditDemande = (demande) => {
        if (!demande || ["APPROVED", "REJECTED", "CANCELLED"].includes(demande.status)) return false;
        if (demande.workflowLevel === "both") {
            return demande.status === "PENDING_MANAGER" || (demande.status === "PENDING_ADMIN" && demande.managerDecision == null);
        }
        return demande.status === "PENDING_MANAGER" || demande.status === "PENDING_ADMIN";
    };

    const canCancelDemande = canEditDemande;

    const buildInitialMission = (demande) => ({
        natureMissionId: demande.natureMissionId || "",
        code: demande.ordreMissionCode || "",
        libelle: demande.ordreMissionLibelle || "",
        description: demande.ordreMissionDescription || "",
        objetMission: demande.ordreMissionObjet || "",
        dateDebut: demande.ordreMissionDateDebut || "",
        dateFin: demande.ordreMissionDateFin || "",
        zoneMission: demande.ordreMissionZoneMission || "EN_VILLE",
        pays: demande.ordreMissionPays || "",
        ville: demande.ordreMissionVille || "",
        adresse: demande.ordreMissionAdresse || "",
        engagementsCollaborateur: demande.ordreMissionEngagementsCollaborateur || "",
        workflow: demande.workflowLevel || "both",
    });

    const openEdit = (demande) => {
        setSelectedDemande(demande);
        setShowEditModal(true);
    };

    const handleEditSubmit = async (payload) => {
        if (!selectedDemande) return;
        try {
            await axios.put(
                `${API_BASE_URL}/api/mission-requests/requests/${selectedDemande.id}/ordre-mission`,
                payload.ordreMission,
                { headers }
            );
            setShowEditModal(false);
            setSelectedDemande(null);
            dispatch(getMyMissionRequestsRequest());
            Swal.fire({
                icon: "success",
                title: "Succès",
                text: "Ordre de mission modifié",
                timer: 1600,
                showConfirmButton: false,
            });
        } catch (error) {
            const message = error?.response?.data?.message || error?.response?.data?.error || error?.message || "Erreur modification ordre de mission";
            Swal.fire({ icon: "error", title: "Erreur", text: message, confirmButtonColor: "#d33" });
        }
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const handleDownloadPdf = async (demande) => {
        try {
            const doc = new jsPDF();

            const loadImage = (url) => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.src = url;
                    img.onload = () => resolve(img);
                    img.onerror = (e) => reject(e);
                });
            };

            try {
                const img = await loadImage(logoImg);
                doc.addImage(img, "PNG", 20, 10, 55, 16);
            } catch (err) {
                console.error("Failed to load logo", err);
            }

            // Title: ORDRE DE MISSION
            doc.setFont("helvetica", "bold");
            doc.setFontSize(14);
            doc.setTextColor(32, 67, 133);
            doc.text("ORDRE DE MISSION", 20, 42);

            // Ref.
            doc.setFont("helvetica", "normal");
            doc.setFontSize(14);
            doc.setTextColor(32, 67, 133);
            doc.text(`Ref. ${demande.ordreMissionCode || "N/A"}`, 20, 50);

            let y = 65;

            // Helper function for sections
            const addSectionTitle = (title) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(12);
                doc.setTextColor(32, 67, 133);
                doc.text(title, 20, y);
                y += 7;
            };

            const addField = (label, value) => {
                doc.setFont("helvetica", "bold");
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                const labelText = label + " : ";
                doc.text(labelText, 20, y);

                const labelWidth = doc.getTextWidth(labelText);
                doc.setFont("helvetica", "normal");
                doc.text(value || "", 20 + labelWidth, y);
                y += 6;
            };

            const addTextParagraph = (text) => {
                doc.setFont("helvetica", "normal");
                doc.setFontSize(10);
                doc.setTextColor(0, 0, 0);
                const splitText = doc.splitTextToSize(text || "", 170);
                doc.text(splitText, 20, y);
                y += (splitText.length * 5) + 3;
            };

            // Section 1: Identification du collaborateur
            addSectionTitle("1. Identification du collaborateur");
            addField("Nom et prénom", demande.employeeName || `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || "Collaborateur");
            addField("Fonction", user?.jobName || "Collaborateur");
            addField("CIN / Passeport", user?.cin || "-");
            y += 4;

            // Section 2: Objet de la mission
            addSectionTitle("2. Objet de la mission");
            doc.setFont("helvetica", "bold");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("Intitulé / objectif de la mission :", 20, y);
            y += 5;
            addTextParagraph(demande.ordreMissionLibelle + (demande.ordreMissionObjet ? ` - ${demande.ordreMissionObjet}` : ""));
            y += 4;

            // Section 3: Lieu et durée de la mission
            addSectionTitle("3. Lieu et durée de la mission");

            // Location
            const lieuParts = [demande.ordreMissionPays, demande.ordreMissionVille, demande.ordreMissionAdresse].filter(Boolean);
            const lieuText = lieuParts.length > 0 ? lieuParts.join(", ") : (demande.ordreMissionLieu || "-");
            addField("Lieu de la mission", lieuText);

            addField("Date de départ", formatDate(demande.ordreMissionDateDebut));
            addField("Date de retour", formatDate(demande.ordreMissionDateFin));

            const duration = calculateDurationDays(demande.ordreMissionDateDebut, demande.ordreMissionDateFin);
            addField("Durée totale", duration ? `${duration} jour${duration > 1 ? "s" : ""}` : "-");
            y += 4;

            // Section 4: Prise en charge et conditions
            addSectionTitle("4. Prise en charge et conditions");
            addTextParagraph(
                "Les frais liés à la mission (transport, hébergement, restauration et autres frais professionnels) sont pris en charge par la société, conformément à la politique interne et sur présentation des justificatifs."
            );
            y += 4;

            // Section 5: Engagement collaborateur
            addSectionTitle("5. Engagement collaborateur");
            doc.setFont("helvetica", "normal");
            doc.setFontSize(10);
            doc.setTextColor(0, 0, 0);
            doc.text("Le collaborateur s'engage à :", 20, y);
            y += 6;
            addTextParagraph(demande.ordreMissionEngagementsCollaborateur || "-");
            y += 5;

            // Sign/Footer
            doc.setFont("helvetica", "normal");
            doc.setFontSize(11);
            doc.text(
                `Fait à Casablanca, le ${new Date(demande.createdAt).toLocaleDateString("fr-FR", {
                    day: "2-digit",
                    month: "long",
                    year: "numeric"
                })}`,
                120,
                y
            );

            doc.save(`Ordre_Mission_${demande.ordreMissionCode || demande.id}.pdf`);
        } catch (err) {
            console.error("PDF generation error", err);
            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: "Une erreur est survenue lors de la génération du PDF.",
                confirmButtonColor: "#ef4444"
            });
        }
    };

    const renderLocation = (demande) => {
        const parts = [demande.ordreMissionPays, demande.ordreMissionVille, demande.ordreMissionAdresse]
            .filter(Boolean);
        return parts.length > 0 ? parts.join(", ") : demande.ordreMissionLieu;
    };

    return (
        <div className="pcPage">
            {/* ── Header ── */}
            <div style={{
                margin: "0 18px 10px 18px",
                padding: "18px 20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 20,
                borderBottom: "1px solid #f1f5f9",
            }}>
                <div>
                    <h1 style={{ margin: 0, color: "var(--admin-primary)", fontSize: 19, fontWeight: 700, lineHeight: 1.2 }}>
                        Mes ordres de mission
                    </h1>
                    <p style={{ margin: "5px 0 0", color: "#94a3b8", fontSize: 12 }}>
                        Soumettez une nouvelle demande et suivez son avancement.
                    </p>
                </div>
                <button
                    onClick={() => setShowModal(true)}
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 7,
                        padding: "9px 16px",
                        borderRadius: 9,
                        border: "none",
                        background: "var(--admin-accent)",
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 600,
                        cursor: "pointer",
                        boxShadow: "0 1px 3px rgba(0,0,0,.12), 0 1px 2px rgba(0,0,0,.06)",
                        transition: "filter .15s",
                        whiteSpace: "nowrap",
                        flexShrink: 0,
                    }}
                    onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.08)"}
                    onMouseLeave={e => e.currentTarget.style.filter = "brightness(1)"}
                >
                    <Plus size={15} />
                    Saisir une nouvelle demande d'ordre de mission
                </button>
            </div>

            {/* ── Card ── */}
            <div style={{ padding: "0 18px 28px" }}>
                <div style={{
                    background: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: 12,
                    boxShadow: "0 1px 3px rgba(16,24,40,.05)",
                    overflow: "hidden",
                }}>
                    {/* Card header */}
                    <div style={{
                        padding: "12px 16px",
                        borderBottom: "1px solid #f1f5f9",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                    }}>
                        <h2 style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "var(--admin-primary)" }}>
                            Historique des demandes
                        </h2>
                        {myRequests.length > 0 && (
                            <span style={{
                                padding: "2px 9px",
                                borderRadius: 999,
                                background: "#f0f4ff",
                                color: "var(--admin-accent)",
                                fontSize: 11,
                                fontWeight: 700,
                                border: "1px solid #dde6fb",
                            }}>
                                {myRequests.length} demande{myRequests.length > 1 ? "s" : ""}
                            </span>
                        )}
                    </div>

                    {/* Content */}
                    <div style={{ padding: 14 }}>
                        {loading && myRequests.length === 0 ? (
                            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
                                <Loader size={28} className="spin" style={{ color: "var(--admin-accent)", opacity: .7 }} />
                            </div>
                        ) : myRequests.length === 0 ? (
                            <div style={{
                                padding: "40px 20px",
                                textAlign: "center",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                gap: 10,
                                color: "#94a3b8",
                            }}>
                                <div style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 12,
                                    background: "#f8fafc",
                                    border: "1px solid #e5e7eb",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}>
                                    <FileText size={22} style={{ opacity: .5 }} />
                                </div>
                                <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280" }}>Aucune demande enregistrée</div>
                                {/*<div style={{ fontSize: 12, color: "#94a3b8" }}>Cliquez sur « Nouvelle demande » pour commencer.</div>*/}
                            </div>
                        ) : (
                            <div style={{ width: "100%", overflowX: "auto", borderRadius: 10, border: "1px solid #eef1f5" }}>
                                <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0, minWidth: 920 }}>
                                    <thead>
                                    <tr>
                                        {["Code", "Mission", "Nature", "Période", "Lieu", "Statut", ""].map((h, i) => (
                                            <th key={i} style={{
                                                background: "#f8f9fa",
                                                color: "#6b7280",
                                                fontSize: 11,
                                                fontWeight: 700,
                                                textAlign: i === 6 ? "right" : "left",
                                                padding: "10px 12px",
                                                borderBottom: "1px solid #e5e7eb",
                                                textTransform: "uppercase",
                                                letterSpacing: ".04em",
                                                whiteSpace: "nowrap",
                                            }}>{h}</th>
                                        ))}
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {myRequests.map((d, idx) => (
                                        <tr key={d.id} style={{ background: idx % 2 === 0 ? "#fff" : "#fcfcfd" }}>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                <span style={{
                                                    display: "inline-block",
                                                    padding: "3px 9px",
                                                    borderRadius: 6,
                                                    background: "#f0f4ff",
                                                    color: "var(--admin-accent)",
                                                    fontSize: 11,
                                                    fontWeight: 700,
                                                    border: "1px solid #dde6fb",
                                                    fontFamily: "monospace",
                                                }}>
                                                    {d.ordreMissionCode}
                                                </span>
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                <div style={{ fontWeight: 600, color: "var(--admin-primary)", fontSize: 12 }}>
                                                    {d.ordreMissionLibelle}
                                                    {d.urgent && (
                                                        <span style={{
                                                            marginLeft: 6,
                                                            padding: "1px 6px",
                                                            borderRadius: 4,
                                                            background: "#fee2e2",
                                                            color: "#b91c1c",
                                                            fontSize: 10,
                                                            fontWeight: 700,
                                                        }}>URGENT</span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                                                    Demandée le {formatDate(d.createdAt)}
                                                </div>
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#374151" }}>
                                                {d.natureMissionLibelle || <span style={{ color: "#d1d5db" }}>—</span>}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", fontSize: 12, color: "#374151", whiteSpace: "nowrap" }}>
                                                {formatDate(d.ordreMissionDateDebut)}
                                                <span style={{ margin: "0 5px", color: "#d1d5db" }}>→</span>
                                                {formatDate(d.ordreMissionDateFin)}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                {renderLocation(d) ? (
                                                    <div style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: "#475569" }}>
                                                        <MapPin size={11} style={{ color: "#94a3b8", flexShrink: 0 }} />
                                                        {renderLocation(d)}
                                                    </div>
                                                ) : <span style={{ color: "#d1d5db" }}>—</span>}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9" }}>
                                                {getStatutBadge(d.status)}
                                            </td>
                                            <td style={{ padding: "11px 12px", borderBottom: "1px solid #f1f5f9", textAlign: "right", display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                                <button
                                                    onClick={() => showDetails(d)}
                                                    title="Détails"
                                                    aria-label="Détails"
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: 32,
                                                        height: 32,
                                                        padding: 0,
                                                        borderRadius: 7,
                                                        border: "1px solid #e2e8f0",
                                                        background: "#fff",
                                                        color: "#475569",
                                                        cursor: "pointer",
                                                        transition: "all .15s",
                                                    }}
                                                    onMouseEnter={e => { e.currentTarget.style.borderColor = "var(--admin-accent)"; e.currentTarget.style.color = "var(--admin-accent)"; e.currentTarget.style.background = "#f0f4ff"; }}
                                                    onMouseLeave={e => { e.currentTarget.style.borderColor = "#e2e8f0"; e.currentTarget.style.color = "#475569"; e.currentTarget.style.background = "#fff"; }}
                                                >
                                                    <Eye size={12} />
                                                </button>
                                                {canEditDemande(d) && (
                                                    <button
                                                        onClick={() => openEdit(d)}
                                                        title="Modifier l'ordre de mission"
                                                        aria-label="Modifier l'ordre de mission"
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 32,
                                                            height: 32,
                                                            padding: 0,
                                                            borderRadius: 7,
                                                            border: "1px solid #bfdbfe",
                                                            background: "#eff6ff",
                                                            color: "#204385",
                                                            cursor: "pointer",
                                                            transition: "all .15s",
                                                        }}
                                                    >
                                                        <Edit size={12} />
                                                    </button>
                                                )}
                                                {canCancelDemande(d) && (
                                                    <button
                                                        onClick={() => handleCancel(d.id)}
                                                        disabled={loading}
                                                        title="Annuler la demande"
                                                        aria-label="Annuler la demande"
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            width: 32,
                                                            height: 32,
                                                            padding: 0,
                                                            borderRadius: 7,
                                                            border: "1px solid #fca5a5",
                                                            background: "#fff7f7",
                                                            color: "#dc2626",
                                                            cursor: loading ? "not-allowed" : "pointer",
                                                            transition: "all .15s",
                                                            opacity: loading ? .65 : 1,
                                                        }}
                                                        onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#fef2f2"; }}
                                                        onMouseLeave={e => { e.currentTarget.style.background = "#fff7f7"; }}
                                                    >
                                                        <AlertCircle size={12} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => handleDuplicate(d)}
                                                    disabled={loading}
                                                    title="Dupliquer"
                                                    aria-label="Dupliquer"
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        width: 32,
                                                        height: 32,
                                                        padding: 0,
                                                        borderRadius: 7,
                                                        border: "1px solid #c7d2fe",
                                                        background: "#eef2ff",
                                                        color: "#204385",
                                                        cursor: loading ? "not-allowed" : "pointer",
                                                        transition: "all .15s",
                                                        opacity: loading ? .65 : 1,
                                                    }}
                                                    onMouseEnter={e => { if (!loading) e.currentTarget.style.background = "#e0e7ff"; }}
                                                    onMouseLeave={e => { e.currentTarget.style.background = "#eef2ff"; }}
                                                >
                                                    <Copy size={12} />
                                                </button>
                                                {d.status === "APPROVED" && (
                                                    <button
                                                        style={{
                                                            width: 32,
                                                            height: 32,
                                                            padding: 0,
                                                            borderRadius: 7,
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            border: "1px solid #16a34a",
                                                            color: "#16a34a",
                                                            background: "#f0fdf4",
                                                            cursor: "pointer"
                                                        }}
                                                        onClick={() => handleDownloadPdf(d)}
                                                        title="Télécharger"
                                                        aria-label="Télécharger"
                                                    >
                                                        <Download size={12} />
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Modal Nouvelle demande ── */}
            {showModal && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Nouvelle demande d'ordre de mission</div>
                                <div className="pcModal__subtitle">Saisissez les détails de l'ordre de mission.</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => setShowModal(false)}
                                aria-label="Fermer"
                            >
                                <X size={15} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <MissionInlineForm
                                formId="missionForm"
                                onSubmit={handleSubmit}
                                loading={loading}
                                submitLabel="Soumettre la demande"
                            />
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel pcModal__panel--wide">
                        <div className="pcModal__header">
                            <div className="pcModal__headerLeft">
                                <div className="pcModal__title">Modifier l'ordre de mission</div>
                                <div className="pcModal__subtitle">Modification autorisée tant que le premier niveau n'a pas traité la demande.</div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => { setShowEditModal(false); setSelectedDemande(null); }}
                                aria-label="Fermer"
                            >
                                <X size={15} />
                            </button>
                        </div>
                        <div className="pcModal__body">
                            <MissionInlineForm
                                formId="editMissionForm"
                                onSubmit={handleEditSubmit}
                                loading={loading}
                                submitLabel="Enregistrer les modifications"
                                initialMissionData={buildInitialMission(selectedDemande)}
                                initialUrgent={selectedDemande.urgent}
                                initialMotif={selectedDemande.motif}
                                showNotes={false}
                                showJustificatifs={false}
                                showComment={false}
                            />
                        </div>
                    </div>
                </div>
            )}

            {/* ── Modal Détails ── */}
            {showDetailsModal && selectedDemande && (
                <div className="pcModal">
                    <div className="pcModal__panel" style={{ width: "min(660px, 100%)" }}>
                        {/* Header */}
                        <div style={{
                            padding: "14px 18px",
                            borderBottom: "1px solid #e5e7eb",
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            gap: 10,
                        }}>
                            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                <div style={{ fontSize: 14, fontWeight: 700, color: "var(--admin-primary)" }}>
                                    {selectedDemande.ordreMissionLibelle || `Demande #${selectedDemande.id}`}
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <span style={{
                                        padding: "2px 8px",
                                        borderRadius: 5,
                                        background: "#f0f4ff",
                                        color: "var(--admin-accent)",
                                        fontSize: 11,
                                        fontWeight: 700,
                                        fontFamily: "monospace",
                                        border: "1px solid #dde6fb",
                                    }}>{selectedDemande.ordreMissionCode}</span>
                                    {getStatutBadge(selectedDemande.status)}
                                </div>
                            </div>
                            <button
                                className="pcIconBtn pcIconBtn--close"
                                onClick={() => setShowDetailsModal(false)}
                                aria-label="Fermer"
                            >
                                <X size={15} />
                            </button>
                        </div>

                        {/* Body */}
                        <div style={{ padding: "18px 18px", overflowY: "auto", display: "flex", flexDirection: "column", gap: 16, maxHeight: "calc(90vh - 130px)" }}>

                            {/* Info grid */}
                            <div style={{
                                background: "linear-gradient(135deg, #f0fdf4, #f8faff)",
                                border: "1px solid #d1fae5",
                                borderRadius: 12,
                                padding: "14px 16px",
                            }}>
                                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: ".07em", color: "#059669", marginBottom: 12 }}>
                                    Informations de la mission
                                </div>
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
                                    {[
                                        { key: "Nature", val: selectedDemande.natureMissionLibelle },
                                        { key: "Date début", val: formatDate(selectedDemande.ordreMissionDateDebut) },
                                        { key: "Date fin", val: formatDate(selectedDemande.ordreMissionDateFin) },
                                        { key: "Durée", val: `${calculateDurationDays(selectedDemande.ordreMissionDateDebut, selectedDemande.ordreMissionDateFin) || "—"} jours` },
                                        { key: "Lieu", val: renderLocation(selectedDemande) || "—" },
                                    ].map(({ key, val }) => (
                                        <div key={key} style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                                            <div style={{ fontSize: 10, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".05em" }}>{key}</div>
                                            <div style={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{val || "—"}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Text fields */}
                            {[
                                { label: "Objet de la mission", val: selectedDemande.ordreMissionObjet },
                                { label: "Engagements du collaborateur", val: selectedDemande.ordreMissionEngagementsCollaborateur },
                                { label: "Commentaire", val: selectedDemande.motif },
                            ].map(({ label, val }) => (
                                <div key={label} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                    <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>
                                        {label}
                                    </label>
                                    <div style={{
                                        whiteSpace: "pre-wrap",
                                        padding: "10px 12px",
                                        background: "#f8fafc",
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 8,
                                        fontSize: 12,
                                        color: val ? "#374151" : "#cbd5e1",
                                        fontStyle: val ? "normal" : "italic",
                                        lineHeight: 1.6,
                                    }}>
                                        {val || "Non renseigné"}
                                    </div>
                                </div>
                            ))}

                            {/* Pièces Jointes / Justificatifs */}
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                <label style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".05em" }}>
                                    Pièces jointes / Justificatifs
                                </label>
                                <div style={{
                                    padding: "10px 12px",
                                    background: "#f8fafc",
                                    border: "1px solid #e2e8f0",
                                    borderRadius: 8,
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 8,
                                }}>
                                    {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 ? (
                                        selectedDemande.justificatifs.map((path, idx) => {
                                            const filename = path.split("/").pop().replace(/^[0-9a-fA-F-]{36}_/, "");
                                            const downloadUrl = `${API_BASE_URL}/api/files/download?path=${encodeURIComponent(path)}`;
                                            return (
                                                <a
                                                    key={idx}
                                                    href={downloadUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{
                                                        display: "inline-flex",
                                                        alignItems: "center",
                                                        gap: 6,
                                                        fontSize: 12,
                                                        fontWeight: 600,
                                                        color: "var(--admin-accent)",
                                                        textDecoration: "none",
                                                    }}
                                                    onMouseEnter={e => e.currentTarget.style.textDecoration = "underline"}
                                                    onMouseLeave={e => e.currentTarget.style.textDecoration = "none"}
                                                >
                                                    <Download size={13} />
                                                    {filename}
                                                </a>
                                            );
                                        })
                                    ) : (
                                        <span style={{ fontSize: 12, color: "#cbd5e1", fontStyle: "italic" }}>Aucune pièce jointe</span>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Footer */}
                        <div style={{
                            padding: "12px 18px",
                            borderTop: "1px solid #e5e7eb",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 10,
                            background: "#fafbfc",
                        }}>
                            <div>
                                {canCancelDemande(selectedDemande) && (
                                    <button
                                        onClick={() => { setShowDetailsModal(false); handleCancel(selectedDemande.id); }}
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 6,
                                            padding: "7px 14px",
                                            borderRadius: 8,
                                            border: "1px solid #fca5a5",
                                            background: "#fff",
                                            color: "#dc2626",
                                            fontSize: 12,
                                            fontWeight: 600,
                                            cursor: "pointer",
                                            transition: "all .15s",
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.background = "#fef2f2"; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = "#fff"; }}
                                    >
                                        <AlertCircle size={13} />
                                        Annuler la demande
                                    </button>
                                )}
                            </div>
                            <button
                                onClick={() => setShowDetailsModal(false)}
                                style={{
                                    padding: "7px 16px",
                                    borderRadius: 8,
                                    border: "1px solid #e2e8f0",
                                    background: "#fff",
                                    color: "#374151",
                                    fontSize: 12,
                                    fontWeight: 600,
                                    cursor: "pointer",
                                    transition: "background .15s",
                                }}
                                onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                                onMouseLeave={e => e.currentTarget.style.background = "#fff"}
                            >
                                Fermer
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MesOrdres;
